export class SquareValue {
    constructor(letter) {
        this.letter = letter
        this.selected = false
        this.notes = []
    }

    static fromJson(j) {
        const v = new SquareValue(j.letter);
        v.selected = j.selected;
        v.notes = j.notes;
        return v;
    }

    static arrayFromLetters(letters) {
        const values = []
        for (var l=0; l < letters.length; l++) {
            values.push(new SquareValue(letters[l]));
        }
        return values;
    }

    setLetter(letter) {
        this.letter = letter
    }

    toggleNote(letter) {
        const index = this.notes.indexOf(letter);
        if (index == -1) {
            // Note NOT present, add
            this.notes.push(letter);
        } else {
            // Note present, remove
            this.notes.splice(index, 1);
        }    
    }

    removeNote(letter) {
        const index = this.notes.indexOf(letter);
        if (index != -1) {
            // Note present, remove
            this.notes.splice(index, 1);
        }    
    }
}

export class SquareValues {
    constructor(dateId, rows, secondsPlayed, complete) {
        this.dateId = dateId
        this.secondsPlayed = secondsPlayed;
        this.rows = rows
        this.complete = complete;
    }

    static fromJson(j) {
        let newRows = [];
        for (var r=0; r < j.rows.length; r++) {
            const jRow = j.rows[r];
            let thisRow = [];
            
            for (var c=0; c < jRow.length; c++) {
                thisRow.push(SquareValue.fromJson(jRow[c]))
            }

            newRows.push(thisRow);
        }

        return new SquareValues(j.dateId, newRows, j.secondsPlayed, j.complete);
    }

    setLetter(row, col, letter) {
        this.rows[row][col].setLetter(letter)
    }

    setSelectedLetter(letter) {
        for (var row in this.rows) {
            for (var col in this.rows[row]) {
                if (this.rows[row][col].letter == letter) {
                    this.rows[row][col].letter = "";
                }
                if (this.rows[row][col].selected) {
                    this.setLetter(row, col, letter);
                }
            }
        }
        
        this.removeNoteEverywhere(letter)


    }

    toggleSelectedNote(letter) {
        this.forEvery((v, _, __) => {
            if (v.selected) {
              v.toggleNote(letter)
            }
        })
    }

    unselectAll() {
        for (var row in this.rows) {
            for (var col in this.rows[row]) {
                this.rows[row][col].selected = false
            }
        }
    }

    select(row, col) {
        this.rows[row][col].selected = true
    }

    canSelect(row, col) {
        return !isFixedPosition(row, col)
    }

    getUsedLetters() {
        let letters = []
        for (var row in this.rows) {
            for (var col in this.rows[row]) {
                let l = this.rows[row][col].letter
                if (l != "") {
                    letters.push(l);
                }
            }
        }

        return letters;
    }

    removeNoteEverywhere(letter) {
        this.forEvery((v, _, __) => {
            v.removeNote(letter)
        })
    }

    forEvery(everyFunc) {
        for (var row in this.rows) {
            for (var col in this.rows[row]) {
                everyFunc(this.rows[row][col], row, col)
            }
        }        
    }

    findLetter(letter) {
        // findLetter returns the row,col of the letter,
        // IF it is currently placed in the puzzle by the user
        // If not found, returns -1,-1

        let row = -1
        let col = -1
        this.forEvery((v, r, c) => {
            if (v.letter == letter) {
                row = r
                col = c
            }
        })

        return [row, col]
    }

    letterUsed(letter) {
        const [row, _] = this.findLetter(letter);
        return row != -1;
    }

    isAdjacentCoords(row1, col1, row2, col2) {
        return Math.abs(row2-row1) <= 1 && Math.abs(col2-col1) <= 1
    }

    isAdjacent(letter1, letter2) {
        const [row1, col1] = this.findLetter(letter1)
        if (row1 == -1) { return false }

        const [row2, col2] = this.findLetter(letter2)
        if (row2 == -1) { return false }

        return this.isAdjacentCoords(row1, col1, row2, col2)
    }

    findWord(word) {
        // findWord returns true if all letters are placed and each pair of letters are adjacent
        for (var l=1; l < word.length; l++) {
            if (!this.isAdjacent(word[l-1], word[l])) {
                return false
            }
        }

        return true
    }

    toJson() {
        return JSON.stringify(this);
    }
}

export class PuzzleWord {
    constructor(word) {
        this.word = word
        this.found = false
    }
}

export class Puzzle {
    constructor(dateId, number, answerGrid, words) {
        this.dateId = dateId
        this.number = number
        this.answerGrid = answerGrid
        this.words = words
    }

    startingValues() {
        const squareValues = []
        for (var row=0; row<5; row++) {
            let rowSquareValues = [];
            for (var col=0; col<5; col++) {
                rowSquareValues.push(isFixedPosition(row, col) ? new SquareValue(this.answerAt(row, col)) : new SquareValue(""))
            }
            squareValues.push(rowSquareValues)
        }
        return new SquareValues(this.dateId, squareValues, 0, false);
    }

    getTypeLetters(fixed) {
        const fixedLetters = [];
        for (var row=0; row<5; row++) {
            for (var col=0; col<5; col++) {
                if (fixed == isFixedPosition(row, col)) {
                    fixedLetters.push(this.answerAt(row, col))
                }
            }
        }

        return fixedLetters
    }

    getFixedLetters(){
        return this.getTypeLetters(true)
    }

    getUnfixedLetters(){
        return this.getTypeLetters(false)
    }

    answerAt(row, col) {
        return this.answerGrid[row*5 + col]
    }

    monospaceChar(c) {
        return "𝙰𝙱𝙲𝙳𝙴𝙵𝙶𝙷𝙸𝙹𝙺𝙻𝙼𝙽𝙾𝙿𝚀𝚁𝚂𝚃𝚄𝚅𝚆𝚇𝚈𝚉"["ABCDEFGHIJKLMNOPQRSTUVWXYZ".indexOf(c)]
    }

    monospaceAnswerAt(row, col) {
        return this.monospaceChar(this.answerAt(row, col))
    }

    redactedMonospacePuzzleText() {
        // "─"
        // 
        let result = this.monospaceAnswerAt(0, 0) + "─" + this.monospaceAnswerAt(0, 2) + "─" + this.monospaceAnswerAt(0, 4) + "\n";
        result += "│╳│╳│\n";
        result += this.monospaceAnswerAt(2, 0) + "─" + this.monospaceAnswerAt(2, 2) + "─" + this.monospaceAnswerAt(4, 4) + "\n";
        result += "│╳│╳│\n";
        result += this.monospaceAnswerAt(4, 0) + "─" + this.monospaceAnswerAt(4, 2) + "─" + this.monospaceAnswerAt(4, 4) + "\n";
        return result;
    }
}

function isFixedPosition(row, col) {
    return col % 2 == 0 && row % 2 == 0
}